'use client'

import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { getAccount, logoutCurrent } from '@/lib/auth'

const DEBUG = String(process.env.NEXT_PUBLIC_DEBUG_AUTH || '').toLowerCase() === 'true'
const SUBS_DISABLED = String(process.env.NEXT_PUBLIC_DISABLE_SUBSCRIPTIONS || 'false').toLowerCase() === 'true'
const nlog = (...args) => { if (DEBUG) try { console.log('[NAV]', ...args) } catch {} }

export default function NavBar() {
  const router = useRouter()
  const pathname = usePathname()
  const [email, setEmail] = useState('')
  const [checking, setChecking] = useState(true)
  const [authed, setAuthed] = useState(false)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        // On auth pages, don't call account and reset state
        if (pathname === '/login' || pathname === '/signup') {
          if (mounted) {
            setAuthed(false)
            setEmail('')
            setChecking(false)
          }
          return
        }
        const acc = await getAccount()
        if (!mounted) return
        setEmail(acc?.email || '')
        setAuthed(Boolean(acc))
      } catch {}
      finally {
        if (mounted) setChecking(false)
      }
    })()
    return () => { mounted = false }
  }, [pathname])

  async function onLogout() {
    try {
      nlog('logout click')
      await logoutCurrent()
      router.replace('/login')
    } catch (err) {
      nlog('logout error', err?.message)
      // optionally display a toast or inline error; keeping minimal
    }
  }

  // Hide navbar on auth pages only
  if (pathname === '/login' || pathname === '/signup') return null

  return (
    <header className="sticky top-0 z-10 w-full border-b border-slate-700 bg-slate-900/75 backdrop-blur">
      <nav className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-2">
        <div className="flex items-center gap-3">
          <Link
            href="/dashboard"
            className={`text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 ${pathname === '/dashboard' ? 'ring-2 ring-purple-500' : ''}`}
          >
            Dashboard
          </Link>
          <Link
            href="/campaigns"
            className={`text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 ${pathname === '/campaigns' ? 'ring-2 ring-purple-500' : ''}`}
          >
            Campaigns
          </Link>
          <Link
            href="/analytics"
            className={`text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 ${pathname === '/analytics' ? 'ring-2 ring-purple-500' : ''}`}
          >
            Analytics
          </Link>
          <Link
            href="/opt-ins"
            className={`text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 ${pathname === '/opt-ins' ? 'ring-2 ring-purple-500' : ''}`}
          >
            Opt-ins
          </Link>
          {SUBS_DISABLED ? null : (
            <Link
              href="/subscription"
              className={`text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 ${pathname === '/subscription' ? 'ring-2 ring-purple-500' : ''}`}
            >
              Subscription
            </Link>
          )}
          <Link
            href="/settings"
            className={`text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 ${pathname === '/settings' ? 'ring-2 ring-purple-500' : ''}`}
          >
            Settings
          </Link>
          <a
            href="https://neurozadialer.vercel.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700"
          >
            Auto Dialer
          </a>
        </div>

        <div className="flex items-center gap-3">
          {checking ? null : (
            authed ? (
              <button
                onClick={onLogout}
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 font-semibold text-white hover:opacity-80" style={{backgroundColor: '#8B5CF6'}}
              >
                Log out
              </button>
            ) : (
              <Link
                href="/login"
                className="text-slate-100 no-underline px-3 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700"
              >
                Log in
              </Link>
            )
          )}
        </div>
      </nav>
    </header>
  )
}

